// admin/src/state/adminStore.js
import React, { createContext, useContext, useReducer, useMemo } from 'react';
import useAdminAuth from '../hooks/useAdminAuth';
import { adminFetch } from '../utils/adminFetch';

export const AdminStoreContext = createContext(null);

// ---- Actions
export const actions = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  // Programs
  SET_PROGRAMS: 'SET_PROGRAMS',
  UPSERT_PROGRAM: 'UPSERT_PROGRAM',
  REMOVE_PROGRAM: 'REMOVE_PROGRAM',
  // Result Content
  SET_RESULT_CONTENTS: 'SET_RESULT_CONTENTS',
  UPSERT_RESULT_CONTENT: 'UPSERT_RESULT_CONTENT',
  REMOVE_RESULT_CONTENT: 'REMOVE_RESULT_CONTENT',
  // Questions
  SET_QUESTIONS: 'SET_QUESTIONS',
  UPSERT_QUESTION: 'UPSERT_QUESTION',
  REMOVE_QUESTION: 'REMOVE_QUESTION',
  // UI state
  SET_FILTER: 'SET_FILTER',
  SET_SELECTION: 'SET_SELECTION',
};

const initial = {
  tenantId: 'demo-university',
  loading: false,
  error: null,

  // data
  programs: [],
  resultContents: [],
  questions: [],

  // UI
  filterText: '',
  selectedProgramId: null,
  selectedResultContentId: null,
  selectedQuestionId: null,
};

function reducer(state, action) {
  switch (action.type) {
    case actions.SET_LOADING:
      return { ...state, loading: action.payload, error: null };
    case actions.SET_ERROR:
      return { ...state, error: action.payload, loading: false };

    // Programs
    case actions.SET_PROGRAMS:
      return { ...state, programs: Array.isArray(action.payload) ? action.payload : [] };
    case actions.UPSERT_PROGRAM: {
      const p = action.payload;
      const idx = state.programs.findIndex(x => String(x._id) === String(p._id));
      const next = [...state.programs];
      if (idx >= 0) next[idx] = { ...next[idx], ...p };
      else next.unshift(p);
      return { ...state, programs: next };
    }
    case actions.REMOVE_PROGRAM:
      return { ...state, programs: state.programs.filter(p => String(p._id) !== String(action.payload)) };

    // ResultContent
    case actions.SET_RESULT_CONTENTS:
      return { ...state, resultContents: Array.isArray(action.payload) ? action.payload : [] };
    case actions.UPSERT_RESULT_CONTENT: {
      const r = action.payload;
      const idx = state.resultContents.findIndex(x => String(x._id) === String(r._id));
      const next = [...state.resultContents];
      if (idx >= 0) next[idx] = { ...next[idx], ...r };
      else next.unshift(r);
      return { ...state, resultContents: next };
    }
    case actions.REMOVE_RESULT_CONTENT:
      return { ...state, resultContents: state.resultContents.filter(r => String(r._id) !== String(action.payload)) };

    // Questions
    case actions.SET_QUESTIONS:
      return { ...state, questions: Array.isArray(action.payload) ? action.payload : [] };
    case actions.UPSERT_QUESTION: {
      const q = action.payload;
      const idx = state.questions.findIndex(x => String(x._id) === String(q._id));
      const next = [...state.questions];
      if (idx >= 0) next[idx] = { ...next[idx], ...q };
      else next.unshift(q);
      return { ...state, questions: next };
    }
    case actions.REMOVE_QUESTION:
      return { ...state, questions: state.questions.filter(q => String(q._id) !== String(action.payload)) };

    // UI
    case actions.SET_FILTER:
      return { ...state, filterText: action.payload ?? '' };
    case actions.SET_SELECTION:
      return { ...state, ...action.payload };

    default:
      return state;
  }
}

// Thunks (exposed via context value)
function useActions(dispatch, secret, tenantId) {
  return useMemo(() => ({
    // Programs
    async loadPrograms() {
      dispatch({ type: actions.SET_LOADING, payload: true });
      try {
        const data = await adminFetch(`/api/programs?tenantId=${tenantId}`, { secret });
        dispatch({ type: actions.SET_PROGRAMS, payload: data });
      } catch (e) {
        dispatch({ type: actions.SET_ERROR, payload: e.message });
      } finally {
        dispatch({ type: actions.SET_LOADING, payload: false });
      }
    },
    async saveProgram(doc) {
      const isUpdate = !!doc._id;
      const path = isUpdate 
        ? `/api/programs/${doc._id}?tenantId=${tenantId}` 
        : `/api/programs?tenantId=${tenantId}`;
      const method = isUpdate ? 'PUT' : 'POST';
      const saved = await adminFetch(path, { method, body: { ...doc, tenantId }, secret });
      dispatch({ type: actions.UPSERT_PROGRAM, payload: saved });
      return saved;
    },
    async deleteProgram(id) {
      await adminFetch(`/api/programs/${id}?tenantId=${tenantId}`, { method: 'DELETE', secret });
      dispatch({ type: actions.REMOVE_PROGRAM, payload: id });
    },

    // Result Contents
    async loadResultContents() {
      dispatch({ type: actions.SET_LOADING, payload: true });
      try {
        const data = await adminFetch(`/api/resultContents?tenantId=${tenantId}`, { secret });
        dispatch({ type: actions.SET_RESULT_CONTENTS, payload: data });
      } catch (e) {
        dispatch({ type: actions.SET_ERROR, payload: e.message });
      } finally {
        dispatch({ type: actions.SET_LOADING, payload: false });
      }
    },
    async saveResultContent(doc) {
      const isUpdate = !!doc._id;
      const path = isUpdate 
        ? `/api/resultContents/${doc._id}?tenantId=${tenantId}` 
        : `/api/resultContents?tenantId=${tenantId}`;
      const method = isUpdate ? 'PUT' : 'POST';
      const saved = await adminFetch(path, { method, body: { ...doc, tenantId }, secret });
      dispatch({ type: actions.UPSERT_RESULT_CONTENT, payload: saved });
      return saved;
    },
    async deleteResultContent(id) {
      await adminFetch(`/api/resultContents/${id}?tenantId=${tenantId}`, { method: 'DELETE', secret });
      dispatch({ type: actions.REMOVE_RESULT_CONTENT, payload: id });
    },

    // Questions
    async loadQuestions() {
      dispatch({ type: actions.SET_LOADING, payload: true });
      try {
        const data = await adminFetch(`/api/questions/admin?tenantId=${tenantId}`, { secret });
        dispatch({ type: actions.SET_QUESTIONS, payload: data });
      } catch (e) {
        dispatch({ type: actions.SET_ERROR, payload: e.message });
      } finally {
        dispatch({ type: actions.SET_LOADING, payload: false });
      }
    },
    async saveQuestion(doc) {
      const isUpdate = !!doc._id;
      const path = isUpdate 
        ? `/api/questions/admin/${doc._id}?tenantId=${tenantId}` 
        : `/api/questions/admin?tenantId=${tenantId}`;
      const method = isUpdate ? 'PUT' : 'POST';
      const saved = await adminFetch(path, { method, body: { ...doc, tenantId }, secret });
      dispatch({ type: actions.UPSERT_QUESTION, payload: saved });
      return saved;
    },
    async deleteQuestion(id) {
      await adminFetch(`/api/questions/admin/${id}?tenantId=${tenantId}`, { method: 'DELETE', secret });
      dispatch({ type: actions.REMOVE_QUESTION, payload: id });
    },

    // UI
    setFilter(text) {
      dispatch({ type: actions.SET_FILTER, payload: text });
    },
    setSelection(partial) {
      dispatch({ type: actions.SET_SELECTION, payload: partial });
    },
  }), [secret, tenantId, dispatch]);
}

export function AdminStoreProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initial);
  const auth = useAdminAuth();
  const api = useActions(dispatch, auth.secret, state.tenantId);

  const value = useMemo(() => ({ state, dispatch, actions, api }), [state, api]);
  return <AdminStoreContext.Provider value={value}>{children}</AdminStoreContext.Provider>;
}

// Export the hook that other components are using
export function useAdminStore() {
  const ctx = useContext(AdminStoreContext);
  if (!ctx) {
    throw new Error('useAdminStore must be used inside an AdminStoreProvider');
  }
  return ctx;
}